import React, { useState, useEffect, useCallback } from 'react';
import { Table, LayoutList } from 'lucide-react';
import { dataEntryApi } from '../../services/api';
import DynamicModule from '../../components/common/DynamicModule';

/**
 * DataEntryModulePage — runtime page for a Data Entry module.
 * Loads module config from AppCfg_DE_Modules and renders one tab per configured view.
 * Each view mounts DynamicModule locked to its view type with per-field entryMode overrides.
 *
 * Props:
 *   moduleSlug — matches Module_Slug in AppCfg_DE_Modules (e.g. "commissioning")
 */
const DataEntryModulePage = ({ moduleSlug }) => {
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeViewIndex, setActiveViewIndex] = useState(0);

  const loadModule = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await dataEntryApi.getModule(moduleSlug);
      setModule(res.data);
      setActiveViewIndex(0);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load module');
    } finally {
      setLoading(false);
    }
  }, [moduleSlug]);

  useEffect(() => { loadModule(); }, [loadModule]);

  useEffect(() => {
    const handler = () => loadModule();
    window.addEventListener('pmd-database-changed', handler);
    return () => window.removeEventListener('pmd-database-changed', handler);
  }, [loadModule]);

  if (loading) return <div className="loading">Loading...</div>;

  if (error) {
    return (
      <div className="module-container">
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-red-500)' }}>
          {error}
        </div>
      </div>
    );
  }

  const views = module?.config?.views || [];

  if (views.length === 0) {
    return (
      <div className="module-container">
        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-gray-400)' }}>
          <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>{module?.module_Name || moduleSlug}</p>
          <p>No views configured. Use the Data Entry Composer to set up this module.</p>
        </div>
      </div>
    );
  }

  const sortedViews = [...views].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  const activeView = sortedViews[activeViewIndex] || sortedViews[0];

  // Build fieldOverrides from view's fieldConfig (entryMode per field)
  const buildFieldOverrides = (view) => {
    const overrides = {};
    (view.fieldConfig || []).forEach(fc => {
      if (fc.entryMode && fc.entryMode !== 'editable') {
        overrides[fc.fieldName] = { entryMode: fc.entryMode };
      }
    });
    return overrides;
  };

  const derivedDatasheetColumns = (activeView.fieldConfig || []).filter(fc => fc.fieldType === 'fk-derived');
  if (activeView.viewType === 'datasheet') {
    console.error('[DEMP] view:', activeView.tableName, '| total fields:', (activeView.fieldConfig || []).length, '| derived:', derivedDatasheetColumns.length);
    console.error('[DEMP] ALL fieldTypes:', (activeView.fieldConfig || []).map(fc => `${fc.fieldName}:${fc.fieldType || 'NONE'}`).join(', '));
    if (derivedDatasheetColumns.length > 0) console.error('[DEMP] derived entries:', JSON.stringify(derivedDatasheetColumns, null, 2));
  }

  // For datasheet views, build externalModuleConfig from DEMC fieldConfig so DynamicModule
  // uses the configured column set instead of the table's JSON module config file.
  const buildExternalConfig = (view) => {
    if (view.viewType !== 'datasheet') return null;
    const regularFields = (view.fieldConfig || []).filter(fc => fc.fieldType !== 'fk-derived');
    if (regularFields.length === 0) return null;
    return {
      datasheetPanel: {
        columns: regularFields.map(fc => ({
          fieldName: fc.fieldName,
          label: fc.label || fc.fieldName,
          width: fc.width || 150,
          alignment: fc.alignment || 'left',
          visible: fc.visible !== false
        }))
      }
    };
  };

  const headerTabs = sortedViews.length > 1 ? (
    <>
      {sortedViews.map((view, idx) => (
        <button
          key={view.viewId || idx}
          className={`lvc-config-tab${idx === activeViewIndex ? ' active' : ''}`}
          onClick={() => setActiveViewIndex(idx)}
        >
          {view.viewType === 'datasheet' ? <Table size={14} /> : <LayoutList size={14} />}
          <span>{view.viewLabel || view.tableName}</span>
        </button>
      ))}
    </>
  ) : null;

  return (
    <div className="de-module-page">
      <DynamicModule
        key={`${moduleSlug}-${activeViewIndex}-${activeView.tableName}`}
        tableName={activeView.tableName}
        forceViewType={activeView.viewType}
        fieldOverrides={buildFieldOverrides(activeView)}
        externalModuleConfig={buildExternalConfig(activeView)}
        headerTabs={headerTabs}
        allowBulkDelete={false}
        derivedDatasheetColumns={derivedDatasheetColumns}
      />
    </div>
  );
};

export default DataEntryModulePage;
